import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { endpoint } from "../../api/endpoint";
import { apiHandler } from "../../api";

const DEBOUNCE_DELAY = 500;

export const usePayout = () => {
  const dispatch = useDispatch();
  const { authData, storeId } = useSelector((state) => state.loginSlice);
  const [searchParams, setSearchParams] = useSearchParams();

  const pageFromUrl = parseInt(searchParams.get("page"), 10);
  const initialPage = !isNaN(pageFromUrl) && pageFromUrl > 0 ? pageFromUrl : 1;
  const limitFromUrl = parseInt(searchParams.get("limit"), 10);
  const initialLimit = !isNaN(limitFromUrl) && limitFromUrl > 0 ? limitFromUrl : 10;

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalOrderCount, setTotalOrderCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [itemsPerPage, setItemsPerPage] = useState(initialLimit);

  const [metrics, setMetrics] = useState({});

  const [balanceDetails, setBalanceDetails] = useState({});

  const [filters, setFilters] = useState({
    start_date: "",
    end_date: "",
    settlement_window: "",
    order_id: "",
    order_settlement_status: "",
    buyer_app_id: "",
    search_by: "",
    search_value: "",
  });

  const [ledger, setLedger] = useState([]);
  const [tenantType, setTenantType] = useState(null);
  const debounceTimer = useRef(null);

  const getTenantType = useCallback(async () => {
    try {
      const response = await apiHandler({
        url: `https://ondcidentitybackend.clouddeploy.in/api/v1/tenant/getMSNTenants?iPageSize=1000`,
        method: "GET",
        authToken: authData,
        dispatch,
        storeId,
      });

      if (response?.data?.statusCode === 200) {
        const tenantData = response?.data?.data;
        // Check if we can find current tenant's type
        // The API response contains grouped data, we need to find the current store's type
        if (tenantData && typeof tenantData === "object") {
          // Search through all tenant groups to find the current store
          for (const groupKey in tenantData) {
            const group = tenantData[groupKey];
            if (Array.isArray(group)) {
              const currentTenant = group.find((t) => t.id === storeId);
              if (currentTenant && currentTenant.type) {
                setTenantType(currentTenant.type);
                break;
              }
            }
          }
        }
      }
    } catch (err) {
      //console.error("Error fetching tenant type:", err);
    }
  }, [authData, dispatch, storeId]);

  useEffect(() => {
    getTenantType();
  }, [getTenantType]);

  const getPayouts = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();

      const filterKeys = [
        "start_date",
        "end_date",
        "settlement_window",
        "order_id",
        "order_settlement_status",
        "buyer_app_id",
      ];
      filterKeys.forEach((key) => {
        if (filters[key]) params.append(key, filters[key]);
      });

      if (filters.search_by && filters.search_value) {
        params.append(filters.search_by, filters.search_value.trim());
      }

      params.append("page", currentPage);
      params.append("limit", itemsPerPage);

      const result = await apiHandler({
        url: `${endpoint.GET_PAYOUTS}?${params.toString()}`,
        method: "GET",
        headers: { "Content-Type": "application/json", tenantid: storeId },
        authToken: authData,
        dispatch,
        storeId,
      });

      const apiRes = result?.data?.data;
      if (apiRes) {
        setData(apiRes.buyer_wise || []);
        // Match the pagination count key from your API structure
        setTotalOrderCount(
          result?.data?.pagination?.count || apiRes.buyer_wise?.length || 0,
        );

        if (apiRes.cards) {
          const card = apiRes.cards;
          const toNum = (v) => {
            if (v === null || v === undefined || v === "") return 0;
            const n = Number(v);
            return Number.isFinite(n) ? n : 0;
          };

          // Dynamically map all fields from API response
          const metricsData = {};
          Object.keys(card).forEach((key) => {
            metricsData[key] = toNum(card[key]);
          });
          setMetrics(metricsData);
        } else {
          setMetrics({});
        }
        setLedger(apiRes.balance_sheet || []);
        setBalanceDetails(apiRes.balance_sheet_details || {});
      }
    } catch (err) {
      //console.error("Payout API Error:", err);
    } finally {
      setLoading(false);
    }
  }, [filters, currentPage, itemsPerPage, authData, dispatch, storeId]);

  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    const isDebounced =
      filters.search_value &&
      ["order_id", "buyer_app_id"].includes(filters.search_by);
    setLoading(true); 
    debounceTimer.current = setTimeout(
      () => getPayouts(),
      isDebounced ? DEBOUNCE_DELAY : 0,
    );
    return () => clearTimeout(debounceTimer.current);
  }, [filters, getPayouts]);

  const totalPages = Math.ceil(totalOrderCount / itemsPerPage);
  const startItem =
    totalOrderCount > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;
  const endItem = Math.min(currentPage * itemsPerPage, totalOrderCount);

  const goToPage = useCallback(
    (page) => {
      const pageNum = typeof page === "function" ? page(currentPage) : page;
      const validPage = Math.max(1, pageNum);
      setCurrentPage(validPage);
      const next = new URLSearchParams(window.location.search);
      if (validPage <= 1) {
        next.delete("page");
      } else {
        next.set("page", validPage.toString());
      }
      setSearchParams(next, { replace: true });
    },
    [currentPage, setSearchParams]
  );

  // Sync state if URL search params change
  useEffect(() => {
    const pageInUrl = parseInt(searchParams.get("page"), 10);
    const validPage = !isNaN(pageInUrl) && pageInUrl > 0 ? pageInUrl : 1;
    setCurrentPage((prev) => (prev !== validPage ? validPage : prev));

    const limitInUrl = parseInt(searchParams.get("limit"), 10);
    if (!isNaN(limitInUrl) && limitInUrl > 0) {
      setItemsPerPage((prev) => (prev !== limitInUrl ? limitInUrl : prev));
    }
  }, [searchParams]);

  const handleItemsPerPageChange = useCallback(
    (newItemsPerPage) => {
      const limitNum = Number(newItemsPerPage);
      setItemsPerPage(limitNum);
      setCurrentPage(1);
      const next = new URLSearchParams(window.location.search);
      next.delete("page");
      if (limitNum === 10) {
        next.delete("limit");
      } else {
        next.set("limit", limitNum.toString());
      }
      setSearchParams(next, { replace: true });
    },
    [setSearchParams]
  );

  const paginatedPayouts = useMemo(() => data, [data]);

  return {
    paginatedPayouts,
    loading,
    totalOrderCount,
    filters,
    setFilters,
    metrics,
    ledger,
    balanceDetails,
    totalPages,
    currentPage,
    setCurrentPage: goToPage,
    startItem,
    endItem,
    goToPage,
    itemsPerPage,
    handleItemsPerPageChange,
    tenantType,
  };
};
